import React, { useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import DashboardHeader from '../components/DashboardHeader';
import DashboardSidebar from '../components/DashboardSidebar';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import '../styles/BannedWordsScreen.css';

const BannedWordsScreen = () => {
  const { getBannedWords, addBannedWord, updateBannedWord, deleteBannedWord } = useAuth();
  const location = useLocation();

  const [activeItem, setActiveItem] = useState('banned-words');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newWord, setNewWord] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (location.pathname.includes('/dashboard/banned-words')) {
      setActiveItem('banned-words');
    }
  }, [location.pathname]);

  useEffect(() => {
    fetchWords();
  }, []);

  const fetchWords = async () => {
    setLoading(true);
    const result = await getBannedWords();
    console.log('banned_words result:', result);
    if (result.success) setWords(result.data || []);
    else setError(result.error || 'Failed to load words.');
    setLoading(false);
  };

  const handleAdd = async () => {
    const trimmed = newWord.trim().toLowerCase();
    if (!trimmed) return;
    if (words.some(w => w.word === trimmed)) {
      setError('Word already exists.');
      return;
    }
    setAdding(true);
    setError('');
    const result = await addBannedWord(trimmed);
    if (result.success) {
      setWords(prev => [...prev, result.data].sort((a, b) => a.word.localeCompare(b.word)));
      setNewWord('');
    } else {
      setError(result.error || 'Failed to add word.');
    }
    setAdding(false);
  };

  const handleEditSave = async (id) => {
    const trimmed = editValue.trim().toLowerCase();
    if (!trimmed) return;
    const result = await updateBannedWord(id, trimmed);
    if (result.success) {
      setWords(prev =>
        prev.map(w => w.id === id ? { ...w, word: trimmed } : w)
          .sort((a, b) => a.word.localeCompare(b.word))
      );
      setEditingId(null);
      setEditValue('');
    } else {
      setError(result.error || 'Failed to update word.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this word?')) return;
    const result = await deleteBannedWord(id);
    if (result.success) {
      setWords(prev => prev.filter(w => w.id !== id));
    } else {
      setError(result.error || 'Failed to delete word.');
    }
  };

  const filtered = words.filter(w => w.word.includes(search.trim().toLowerCase()));

  return (
    <div className="bw-screen">
      <DashboardSidebar
        activeItem={activeItem}
        setActiveItem={setActiveItem}
        isOpen={sidebarOpen}
        setIsOpen={setSidebarOpen}
      />
      <div className="bw-content">
        <DashboardHeader
          toggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          title="Banned Words"
          subtitle="Manage profanity filter — add, edit or remove banned words"
        />
        <Container fluid className="bw-container">

          {/* Add Word */}
          <div className="bw-add-row">
            <input
              className="bw-input"
              type="text"
              placeholder="Add new banned word…"
              value={newWord}
              onChange={e => { setNewWord(e.target.value); setError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
            />
            <button className="bw-btn bw-btn-add" onClick={handleAdd} disabled={adding || !newWord.trim()}>
              {adding ? 'Adding…' : '+ Add'}
            </button>
          </div>

          {error && <p className="bw-error">{error}</p>}

          {/* Search */}
          <div className="bw-search-row">
            <input
              className="bw-input bw-search"
              type="text"
              placeholder="Search words…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <span className="bw-count">{filtered.length} word{filtered.length !== 1 ? 's' : ''}</span>
          </div>

          {/* Table */}
          {loading ? (
            <p className="bw-loading">Loading…</p>
          ) : (
            <div className="bw-table-wrapper">
              <table className="bw-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Word</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="bw-empty">No words found</td>
                    </tr>
                  ) : (
                    filtered.map((w, i) => (
                      <tr key={w.id}>
                        <td className="bw-num">{i + 1}</td>
                        <td>
                          {editingId === w.id ? (
                            <input
                              className="bw-input bw-inline-input"
                              value={editValue}
                              onChange={e => setEditValue(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') handleEditSave(w.id);
                                if (e.key === 'Escape') { setEditingId(null); setEditValue(''); }
                              }}
                              autoFocus
                            />
                          ) : (
                            <span className="bw-word">{w.word}</span>
                          )}
                        </td>
                        <td className="bw-actions">
                          {editingId === w.id ? (
                            <>
                              <button className="bw-btn bw-btn-save" onClick={() => handleEditSave(w.id)}>Save</button>
                              <button className="bw-btn bw-btn-cancel" onClick={() => { setEditingId(null); setEditValue(''); }}>Cancel</button>
                            </>
                          ) : (
                            <>
                              <button className="bw-btn bw-btn-edit" onClick={() => { setEditingId(w.id); setEditValue(w.word); }}>Edit</button>
                              <button className="bw-btn bw-btn-delete" onClick={() => handleDelete(w.id)}>Delete</button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Container>
      </div>
    </div>
  );
};

export default BannedWordsScreen;
